import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Binoculars, CircleNotch, Trash, MagnifyingGlass } from "@phosphor-icons/react";

export default function Competitors() {
  const { currentWorkspace } = useAuth();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.listCompetitors().then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [currentWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyze = async () => {
    if (!domain.trim()) { toast.error("Enter a competitor domain"); return; }
    setLoading(true); setActive(null);
    try {
      const r = await api.analyzeCompetitor(domain.trim());
      setActive(r.data); setDomain(""); load();
      toast.success(`Analyzed ${r.data.domain}`);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const del = async (id) => { await api.deleteCompetitor(id); if (active?.id === id) setActive(null); load(); };

  return (
    <div className="fade-up grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
      <div className="space-y-4">
        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <div className="flex items-center gap-3 mb-3"><Binoculars size={22} weight="duotone" color="var(--accent)" /><h2 className="text-base font-bold">Competitor Analysis</h2></div>
          <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>I crawl a rival's site, extract their target keywords, and build a plan to outrank them for your market.</p>
          <input data-testid="competitor-domain" value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && analyze()} placeholder="competitor.com" className="w-full mono text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
          <button data-testid="analyze-competitor" onClick={analyze} disabled={loading} className="w-full mt-3 px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
            {loading ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />} {loading ? "Analyzing…" : "Analyze"}
          </button>
        </div>
        {list.length > 0 && (
          <div className="border p-4 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
            <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-2)" }}>History</h3>
            <div className="space-y-2">
              {list.map((c) => (
                <div key={c.id} data-testid="competitor-row" className="flex items-center justify-between text-sm">
                  <button onClick={() => setActive(c)} className="mono truncate hover:text-white transition-colors text-left" style={{ color: active?.id === c.id ? "var(--accent)" : "var(--text-2)" }}>{c.domain}</button>
                  <button onClick={() => del(c.id)} className="p-1 hover:text-[var(--danger)] transition-colors" style={{ color: "var(--text-2)" }}><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 border bg-[var(--surface)] min-h-[400px]" style={{ borderColor: "var(--line)" }}>
        {loading && <div className="p-16 text-center"><CircleNotch size={30} className="animate-spin mx-auto mb-3" color="var(--accent)" /><p className="mono text-sm" style={{ color: "var(--text-2)" }}>Crawling competitor & building your win plan…</p></div>}
        {!loading && !active && <div className="p-16 text-center"><Binoculars size={34} weight="duotone" color="var(--text-2)" className="mx-auto mb-3" /><p className="text-sm" style={{ color: "var(--text-2)" }}>Analyze a competitor to see their keywords and how to beat them.</p></div>}
        {active && (
          <div>
            <div className="p-4 border-b" style={{ borderColor: "var(--line)" }}>
              <div className="mono text-sm" style={{ color: "var(--accent)" }}>{active.domain}</div>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="competitor-keywords">
                {active.keywords.map((k) => <span key={k} className="mono text-[11px] px-2 py-0.5 border" style={{ borderColor: "var(--line)", color: "var(--text-2)" }}>{k}</span>)}
              </div>
            </div>
            <div className="p-6 prose-cs text-sm"><ReactMarkdown>{active.analysis}</ReactMarkdown></div>
          </div>
        )}
      </div>
    </div>
  );
}
